import { fixDateInstance } from '@libs/util';
import dayjs from 'dayjs';
import { mapFullAddressFromDto } from '../address/helpers';
import { TaskStatus, TaskCategory, } from './models';
const STATUS_MAP = {
    Completed: TaskStatus.Complete,
    Open: TaskStatus.Open,
    Partial: TaskStatus.Partial,
    Cancelled: TaskStatus.Cancelled,
    Unknown: TaskStatus.Unknown,
};
const CATEGORY_MAP = {
    bookPhotographer: TaskCategory.BookPhotographer,
    uploadImages: TaskCategory.UploadImages,
    uploadVideos: TaskCategory.UploadVideos,
    virtualStaging: TaskCategory.VirtualStaging,
    PERSON: TaskCategory.Person,
    BASIC: TaskCategory.Basic,
    BASIC_OPT: TaskCategory.BasicOpt,
    INTRO_OUTRO_SPEAK: TaskCategory.IntroOutroSpeak,
    COPYWRITETEXTS: TaskCategory.CopywriteTexts,
    TEXT: TaskCategory.Text,
    ARIAL: TaskCategory.Arial,
    COMTEXT: TaskCategory.ComText,
    INTRO_OUTRO: TaskCategory.IntroOutro,
    PERSON_INFO: TaskCategory.PersonInfo,
    SPEAK_INPUT: TaskCategory.SpeakInput,
    VIDIBILI_COMMENT: TaskCategory.VidibiliComment,
    combineImages: TaskCategory.CombineImages,
    virtualStaging720: TaskCategory.VirtualStaging720,
    virtualStaging720Commercial: TaskCategory.VirtaulStagingCommercial720,
    virtualStagingCommercial: TaskCategory.VirtualStagingCommercial,
    virtualStagingInDoorRenovation: TaskCategory.VirtualStagingIndoorRenovation,
    virtualStagingOutDoorRenovation: TaskCategory.VirtualStagingOutdoorRenovation,
    virtualStagingRemovalOfObjects: TaskCategory.VirtualStagingRemoveObjects,
    LOCATION: TaskCategory.Location,
    beforeAndAfter: TaskCategory.BeforeAfter,
    'TEXT-STYLE': TaskCategory.NoStyleText,
    SoMeType: TaskCategory.SoMe,
    SoMeSlideshow: TaskCategory.SoMeSlideshow,
    SomeGif: TaskCategory.SomeGif,
    imageSelector: TaskCategory.ImageSelector,
    WhiteboxImageSelector: TaskCategory.WhiteboxImageSelector,
    // TODO: adjust with API name
    AITextInput: TaskCategory.AITextInput,
    AITextOverview: TaskCategory.AITextOverview,
};
export function mapTaskStatusFromDto(dtoStatus) {
    return STATUS_MAP[dtoStatus];
}
export function mapTaskStatusToDto(status) {
    return Object.keys(STATUS_MAP).find(key => STATUS_MAP[key] === status);
}
export function mapTaskCategoryFromDto(dtoCategory) {
    return CATEGORY_MAP[dtoCategory];
}
export function mapTaskCategoryToDto(category) {
    return Object.keys(CATEGORY_MAP).find(key => CATEGORY_MAP[key] === category);
}
export function mapTaskFiltersFromDto({ types, branches, statuses }) {
    return {
        categories: types.map(mapTaskCategoryFromDto),
        statuses: statuses.map(mapTaskStatusFromDto),
        branches,
    };
}
export function mapTaskOrderByParamToDto(orderBy) {
    return (orderBy &&
        {
            createdAt: 'creationdate',
            branchName: 'branchname',
            caseId: 'reference',
            type: 'tasktype',
            status: 'status',
            productName: 'productname',
        }[orderBy]);
}
export function mapVirtualStagingFromDto({ imageUrl, roomType, furnitures }) {
    return {
        roomTypeName: roomType,
        url: imageUrl,
        isSelected: false,
        interiorOptions: furnitures.map(({ code, imageUrl: url }) => ({
            name: code,
            url,
            isSelected: false,
        })),
    };
}
function normalizeBookingPhotographerData({ proposal, duration, bookingId, }) {
    const arrival = new Date(proposal.expectedStart), departure = dayjs(arrival).add(duration, 'm').toDate(), start = new Date(proposal.timeframeStart), end = new Date(proposal.timeframeEnd);
    let area, info;
    if (proposal.notes) {
        const infoStr = proposal.notes.trim();
        const timeRegExp = /\d{4}-\d{2}-\d{2} \d{2}:\d{2}/g;
        const lineBreakExp = /(?:<(?:br|BR) ?\/>)+/g;
        const areaExp = /(?:Area: )(.*)(?= m2)/g;
        // split string in array using <BR/> tag as separator
        const infoArr = infoStr.split(lineBreakExp).filter(str => {
            const parsedArea = str === null || str === void 0 ? void 0 : str.match(areaExp);
            // if element has Area info - get area and exclude this element from info array
            if (parsedArea) {
                area = parsedArea;
                return false;
            }
            else {
                return true;
            }
        });
        // if element has timestamp - cut it and return as separate property
        if (infoArr.length) {
            info = infoArr.map(str => {
                // format always the same
                if (~str.search(timeRegExp)) {
                    return {
                        time: new Date(str.trim().slice(0, 16)),
                        text: str.trim().slice(17),
                    };
                }
                else {
                    return { text: str };
                }
            });
        }
    }
    return {
        start,
        end,
        timeForTask: duration,
        info,
        area,
        arrival,
        departure,
        bookingId,
        id: proposal.proposalId,
        photographerName: proposal.photographerName ? proposal.photographerName : undefined,
    };
}
export function mapCallProposalFromDto(proposalResponse) {
    var _a;
    return {
        primary: proposalResponse.suggestedProposal
            ? normalizeBookingPhotographerData({
                proposal: proposalResponse.suggestedProposal,
                duration: proposalResponse.durationMinutes,
                bookingId: proposalResponse.bookingId,
            })
            : undefined,
        secondary: ((_a = proposalResponse.proposalList) === null || _a === void 0 ? void 0 : _a.map(proposal => normalizeBookingPhotographerData({
            proposal,
            duration: proposalResponse.durationMinutes,
            bookingId: proposalResponse.bookingId,
        }))) || [],
        duration: proposalResponse.durationMinutes,
    };
}
export function mapDatahubTaskFromDto(task) {
    const { branchId, branchName, caseId, caseReference, createdAt, id, productName, status, type, address, orderId, orderlineId, salesId, contactPersonUserID, notes, } = task;
    return {
        _dto: task,
        branchId,
        branchName,
        caseId,
        caseReference,
        createdAt: new Date(fixDateInstance(createdAt)),
        id,
        productName,
        status: mapTaskStatusFromDto(status),
        type: mapTaskCategoryFromDto(type),
        address: address && mapFullAddressFromDto(address),
        orderId,
        orderlineId,
        salesId,
        contactPersonId: contactPersonUserID,
        notes: (notes === null || notes === void 0 ? void 0 : notes.map(mapTextTaskNoteFromDto)) || [],
    };
}
export function mapInterviewQuestionFromDto({ question, yesOrNoQuestion, answer, }) {
    return {
        question,
        answer,
        yesOrNo: yesOrNoQuestion,
    };
}
export function mapTextTaskNoteFromDto(note) {
    return {
        text: note.text,
    };
}
/**
 * Into the canonical `ITask` (`efkt/tasks/models.ts`), for the mixed task list —
 * `v2/modules/tasks/hooks.tsx`. `TaskStatus` is value-identical between the datahub and efkt enums,
 * and datahub's `TaskCategory` is a *subset* of efkt's since AXIT-836 added five `EFKT__*` members —
 * so the cast is still exact in this direction, which is the only one that runs. Do not copy those
 * five over here; datahub cannot produce them. Unlike cases, whose two enums are disjoint.
 * No `_dto` on the canonical model (unlike `department`/`material`/`packages`) —
 * every datahub task write (`v2/modules/tasks/api.ts`'s `tasksApi`) is a narrow `taskId`-scoped call,
 * never a whole-object merge-and-repost, so there is nothing for `_dto` to carry; the canonical model
 * matches `efkt/cases/models.ts`'s `ICase`, which likewise carries none.
 */
export function mapTaskFromDatahubTask(task) {
    var _a, _b;
    const { orderlineId, _dto, ...rest } = task;
    return {
        ...rest,
        sourceBackend: 'datahub',
        status: task.status,
        type: task.type,
        orderLineId: orderlineId,
        // `notes` on the mapped `IDatahubTask` drops `id`/`createdAt` (see `mapTextTaskNoteFromDto`) —
        // the raw dto still has them, which is what the canonical `ITextTaskNote` needs.
        notes: (_b = (_a = _dto.notes) === null || _a === void 0 ? void 0 : _a.map(mapCanonicalTextTaskNoteFromDto)) !== null && _b !== void 0 ? _b : [],
    };
}
function mapCanonicalTextTaskNoteFromDto(note) {
    return {
        id: note.id,
        createdAt: new Date(fixDateInstance(note.createdAt)),
        text: note.text,
    };
}
/** Into the canonical `ITaskFilters` enums, for `useTaskFilterOptions`'s cross-source merge. */
export function mapTaskFiltersFromDatahub(filters) {
    return {
        categories: filters.categories,
        statuses: filters.statuses,
        branches: filters.branches,
    };
}
